import { memo, useEffect } from "react";

import noop from "lodash/noop";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { GRANULARITY_LIST } from "app/pages/activation/components/Header/constants";
import { SectionTitle } from "app/pages/activation/components/Settings/GeneralSettings/styles";
import {
    AlertsSubscriptions,
    ALERTS_SUBSCRIPTIONS_WRAPPER_ID,
} from "app/pages/activation/components/Shared/Subscriptions/AlertsSubscriptions";
import { BenchmarkDigestSubscriptions } from "app/pages/activation/components/Shared/Subscriptions/BenchmarkDigestSubscriptions";
import { useSubscriptionContext } from "app/pages/activation/context/Subscription/SubscriptionProvider";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { generateAutomationName } from "../../../helpers/generateAutomationName";
import type { BenchmarkGranularity } from "../../../types/common";

import { NewsAlerts } from "./NewsAlerts";
import * as SC from "./styles";
import { EMAIL_TEXT } from "./texts";

export const Subscriptions = memo(() => {
    const {
        isSwitchedAlert,
        isSwitchedDigest,
        isSwitchedNewsAlerts,
        isAlertsAvailable,
        alertsGranularity,
        digestGranularity,
        onSelect,
        onSwitch,
    } = useSubscriptionContext();

    const userEmail: string = swSettings.user.username;
    const granularityList = Object.keys(GRANULARITY_LIST) as BenchmarkGranularity[];

    useEffect(() => {
        const { scrollToAlerts } = proNavigator.getParams();

        if (!scrollToAlerts) {
            return;
        }

        const rafId = requestAnimationFrame(() => {
            document.getElementById(ALERTS_SUBSCRIPTIONS_WRAPPER_ID)?.scrollIntoView({ behavior: "smooth" });
        });

        return () => cancelAnimationFrame(rafId);
    }, []);

    return (
        <SC.EmailContainer>
            <SC.EmailInputContainer
                data-automation={generateAutomationName("settings", ["subscriptions", "email", "input", "container"])}
            >
                <SectionTitle
                    marginBottom={12}
                    data-automation={generateAutomationName("settings", ["subscriptions", "email", "input", "title"])}
                >
                    {translate(EMAIL_TEXT)}
                </SectionTitle>

                <SC.EmailInput
                    value={userEmail}
                    isDisabled
                    onChange={noop}
                />
            </SC.EmailInputContainer>

            <BenchmarkDigestSubscriptions
                digestGranularity={digestGranularity}
                granularityList={granularityList}
                isSwitchedDigest={isSwitchedDigest}
                onSwitch={onSwitch}
                onSelect={onSelect}
            />

            <AlertsSubscriptions
                granularityList={granularityList}
                isAlertsAvailable={isAlertsAvailable}
                alertsGranularity={alertsGranularity}
                isSwitchedAlert={isSwitchedAlert}
                onSwitch={onSwitch}
                onSelect={onSelect}
            />

            <NewsAlerts isSwitchedAlertsNews={isSwitchedNewsAlerts} />
        </SC.EmailContainer>
    );
});

Subscriptions.displayName = "Subscriptions";
